import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import { Button, Callout, Modal } from '@/components/UI';
import { useAppQuery } from '@/hooks/useAppQuery';

/** Both ordinary sweeps and study jobs create a child through the same research path. */
const CreateResearchVariantModal = ({
    jobId,
    candidateId,
    summary,
    onClose,
    onAdvanced
}: {
    jobId: number;
    candidateId: number;
    summary: string;
    onClose: () => void;
    onAdvanced: () => void;
}) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const confirmationsQuery = useAppQuery({
        ...trpc.entryOptimizer.listConfirmations.queryOptions({ jobId }),
        refetchInterval: (query) =>
            (query.state.data?.confirmations ?? []).some(
                (confirmation) =>
                    confirmation.candidateId === candidateId && (confirmation.status === 'pending' || confirmation.status === 'running')
            )
                ? 5000
                : false
    });
    const confirmations = (confirmationsQuery.data?.confirmations ?? []).filter((confirmation) => confirmation.candidateId === candidateId);
    const confirmed = confirmations.some((confirmation) => confirmation.status === 'completed' && confirmation.parityVerdict === 'passed');
    const confirming = confirmations.some((confirmation) => confirmation.status === 'pending' || confirmation.status === 'running');
    const confirmationFailed = confirmations.some(
        (confirmation) =>
            confirmation.status === 'failed' || (confirmation.status === 'completed' && confirmation.parityVerdict !== 'passed')
    );
    const requestConfirmation = useMutation({
        ...trpc.entryOptimizer.requestConfirmation.mutationOptions(),
        onSuccess: async () => {
            await queryClient.invalidateQueries(trpc.entryOptimizer.listConfirmations.pathFilter());
        },
        onError: (error) => showToast(error.message, 'error')
    });
    const createVariant = useMutation({
        ...trpc.entryOptimizer.createResearchVariant.mutationOptions(),
        onSuccess: async ({ variant }) => {
            await queryClient.invalidateQueries(trpc.strategyVariant.listFamilies.pathFilter());
            showToast(`Created inactive research variant #${variant.strategyId} in family #${variant.familyId}.`, 'success');
            if (variant.warnings.length > 0) showToast(variant.warnings.join('\n'), 'warning', true);
            onClose();
            void navigate(`/user/tradingStrategy/${variant.strategyId}`);
        },
        onError: (error) => showToast(error.message, 'error')
    });

    return (
        <Modal
            isOpen
            title="Create research variant"
            hideCloseButton={createVariant.isPending}
            closeOnEsc={!createVariant.isPending}
            closeOnOverlayClick={!createVariant.isPending}
            onClose={() => {
                if (!createVariant.isPending) onClose();
            }}
        >
            <div className="flex flex-col gap-4">
                <div className="rounded border border-zinc-800 bg-black/20 p-3">
                    <div className="text-sm font-medium text-zinc-100">Candidate #{candidateId}</div>
                    <p className="mt-1 text-sm text-zinc-400">{summary}</p>
                </div>
                <p className="text-sm text-zinc-300">
                    Create an inactive strategy in the same family with this entry. Backtest it against the saved parent before choosing a
                    treatment. Weak historical performance can still justify this research trial.
                </p>
                <Callout variant={confirmed ? 'success' : 'info'}>
                    {confirmed
                        ? 'The real-entry confirmation passed. This checks that the entry behaves as evaluated; it does not approve live trading.'
                        : confirming
                          ? 'Real-entry confirmation is running. Creation becomes available when it completes and matches the evaluated entry.'
                          : 'Run one real-entry confirmation before creating the variant. This checks entry behavior, not whether the candidate is profitable.'}
                </Callout>
                {!confirmed && confirmationFailed && (
                    <Callout variant="warning">
                        A confirmation failed or found an entry mismatch. Inspect the run details before retrying; this is separate from
                        weak strategy performance.
                    </Callout>
                )}
                {confirmationsQuery.isError && (
                    <Callout variant="error">
                        Confirmation status could not load.{' '}
                        <Button size="sm" variant="outline-dark" onClick={() => void confirmationsQuery.refetch()}>
                            Retry status
                        </Button>
                    </Callout>
                )}
                <details className="text-xs text-zinc-500">
                    <summary className="cursor-pointer">Advanced: source entry publication</summary>
                    <p className="my-2">
                        Use the separate source-publication controls only when you intend to publish a revision of the source entry or
                        replace its attachment.
                    </p>
                    <Button disabled={createVariant.isPending} size="sm" variant="outline-dark" onClick={onAdvanced}>
                        Open source publication controls
                    </Button>
                </details>
                <div className="flex justify-end gap-2">
                    <Button disabled={createVariant.isPending} variant="outline-dark" onClick={onClose}>
                        Cancel
                    </Button>
                    {!confirmed && (
                        <Button
                            disabled={confirming || requestConfirmation.isPending || !confirmationsQuery.isSuccess}
                            onClick={() => requestConfirmation.mutate({ jobId, candidateId })}
                        >
                            {confirming || requestConfirmation.isPending ? 'Confirming…' : 'Confirm real entries'}
                        </Button>
                    )}
                    <Button disabled={!confirmed || createVariant.isPending} onClick={() => createVariant.mutate({ jobId, candidateId })}>
                        {createVariant.isPending ? 'Creating…' : 'Create inactive variant'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default CreateResearchVariantModal;
